// Cookie-consent banner: a slim bottom bar (not a modal — it must not trap
// focus, lock scroll, or block the page). Shows until a choice is stored, or
// again when re-opened via the footer's "Cookie settings" link.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { localizePath } from '@/lib/localeRoutes';
import { useCookieConsent } from '@/hooks/useCookieConsent';

export const CookieConsentBanner = () => {
  const { t, i18n } = useTranslation('common');
  const { choice, isOpen, accept, reject } = useCookieConsent();

  // Render nothing until mounted so the SSG-prerendered HTML and the first
  // client paint agree (localStorage-derived state is client-only).
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted) return null;
  if (choice !== null && !isOpen) return null;

  return (
    <div
      role="region"
      aria-label={t('cookieConsent.ariaLabel')}
      className="consent-banner fixed inset-x-0 bottom-0 z-[60] border-t bg-background/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-2 lg:flex-row lg:items-center sm:gap-4 sm:px-6 lg:px-8">
        <p className="min-w-0 flex-1 break-words text-sm leading-5 text-muted-foreground">
          {t('cookieConsent.message')}{' '}
          <Link
            to={localizePath('/privacy-policy', i18n.language)}
            className="rounded-sm underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {t('cookieConsent.learnMore')}
          </Link>
        </p>
        <div className="grid min-w-0 gap-2 [grid-template-columns:repeat(auto-fit,minmax(min(100%,8rem),1fr))] lg:flex [&>button]:min-w-0">
          <Button variant="outline" size="sm" className="min-h-11 min-w-11" onClick={reject}>
            {t('cookieConsent.reject')}
          </Button>
          <Button size="sm" className="min-h-11 min-w-11" onClick={accept}>
            {t('cookieConsent.accept')}
          </Button>
        </div>
      </div>
    </div>
  );
};
